import React from "react";
import { FileCheck2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(2)}`;

const FSTATUS = {
  filed: ["Filed", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  pending: ["Pending", "bg-slate-100 text-slate-600 border-slate-200"],
  due_soon: ["Due soon", "bg-amber-50 text-amber-800 border-amber-300"],
  overdue: ["Overdue", "bg-rose-50 text-rose-700 border-rose-200"],
};
const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
const monthLabel = (key) => { const [y, m] = key.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" }); };

const Month = ({ m, multiEntity, token }) => (
  <div data-testid={`portal-filings-month-${m.month}`} className={`bg-white border rounded-md ${m.is_current ? "border-blue-700" : "border-slate-200"}`}>
    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
      <div className="text-sm font-semibold">{monthLabel(m.month)}</div>
      {m.is_current && <span className="text-[10px] uppercase tracking-wider text-zinc-950">This month</span>}
    </div>
    {m.filings.length === 0 ? <div className="px-5 py-3 text-xs text-slate-400">No filings due.</div> : (
      <ul className="divide-y divide-slate-100">
        {m.filings.map((f) => {
          const [label, cls] = FSTATUS[f.status];
          return (
            <li key={`${f.name}-${f.entity}-${f.due_date}`} className="px-5 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="portal-filing-row">
              <span className="flex-1 min-w-[180px] text-slate-800">{f.name}{multiEntity && <span className="text-xs text-slate-500"> · {f.entity}</span>}</span>
              <span className="text-xs text-slate-500 mono">Due {fmt(f.due_date)}{f.extended && <span className="text-blue-700" data-testid="portal-filing-extended"> (extended from {fmt(f.standard_due_date)})</span>}</span>
              {f.filed_on && <span className="text-xs text-emerald-700 mono">Filed {fmt(f.filed_on)}</span>}
              <span data-testid="portal-filing-status" className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cls}`}>{label}</span>
              {f.documents?.length > 0 && (
                <div className="w-full flex flex-wrap gap-1.5">
                  {f.documents.map((d) => (
                    <a key={d.id} data-testid={`portal-filing-doc-${d.id}`} href={`${API}/portal/${token}/filing-documents/${d.id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 max-w-[260px]">
                      <Download className="w-3 h-3 shrink-0" /><span className="truncate">{d.filename}</span>
                    </a>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export default function PortalFilings({ months, fy, fyOptions = [], onFy, token }) {
  const all = months.flatMap((m) => m.filings);
  const multiEntity = new Set(all.map((f) => f.entity)).size > 1;
  const open = all.filter((f) => f.status !== "filed").length;
  return (
    <section className="mt-12" data-testid="portal-filings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2"><FileCheck2 className="w-5 h-5" />Statutory filings</h2>
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-md p-1" data-testid="portal-fy-toggle">
          {[null, ...fyOptions].map((y) => (
            <button key={y ?? "recent"} type="button" data-testid={`portal-fy-${y ?? "recent"}`} onClick={() => onFy(y)}
              className={`px-3 py-1 text-xs rounded-sm transition-colors ${fy === y ? "bg-white shadow-sm text-blue-700 font-semibold" : "text-slate-600 hover:text-blue-700"}`}>{y ? fyLabel(y) : "Recent"}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-1" data-testid="portal-filings-summary">
        {all.length ? `${all.length - open} filed · ${open} open ${fy ? `in ${fyLabel(fy)} (April to March)` : "across the last 3 and next 2 months"}` : "No filings tracked for this period yet."}
      </p>
      <div className="mt-5 space-y-3">{(fy ? months : months.slice().reverse()).map((m) => <Month key={m.month} m={m} multiEntity={multiEntity} token={token} />)}</div>
    </section>
  );
}
